import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';

import { SchoolLpContent } from '../models/school-lp-content';

type SchoolHeroSectionProps = NonNullable<StaticSchoolHeroSectionProps>;

export function SchoolHeroSection({ eyebrow, title, description, theme }: SchoolHeroSectionProps) {
  return (
    <Box as='section' overflow='hidden' position='relative'>
      <Box inset={0} position='absolute' aria-hidden>
        <Image
          alt=''
          sizes='100vw'
          src={theme.backgroundImage}
          style={{ objectFit: 'cover', objectPosition: 'bottom right' }}
          fill
          priority
        />
      </Box>
      <ContentContainer contentMaxWidth='content-max-width-md' position='relative'>
        <Flex
          alignItems={{ base: 'flex-start', md: 'center' }}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: '24px', md: '64px' }}
          minHeight={{ md: '360px' }}
          padding={{ base: '0 0 24px', md: '0 0 48px' }}
        >
          <Box
            flexShrink={0}
            height={{ base: '250px', md: '312px' }}
            position='relative'
            width={{ base: '96px', md: '120px' }}
          >
            <Image alt='' sizes='120px' src={theme.pennantImage} style={{ objectFit: 'contain' }} fill priority />
          </Box>
          <Flex flexDirection='column' gap='16px'>
            <Flex flexDirection='column' gap='8px'>
              <Flex alignItems='center' gap='8px'>
                <Text
                  color={theme.eyebrowColor}
                  fontFamily='condensed'
                  fontSize={{ base: '0.8125rem', md: '0.9375rem' }}
                  fontWeight='500'
                  letterSpacing='0.9px'
                  lineHeight='1'
                  textTransform='uppercase'
                  whiteSpace='nowrap'
                >
                  {eyebrow}
                </Text>
                <Box bg={theme.eyebrowColor} flex={1} height='1px' opacity={0.4} />
              </Flex>
              <Heading as='h1' color='white' size={{ base: 'mobile-heading-2', md: 'heading-2' }}>
                {title}
              </Heading>
            </Flex>
            <Text
              color='grey.100'
              fontSize={{ base: '0.875rem', md: '1rem' }}
              lineHeight={{ base: '1.4', md: '1.5' }}
              maxWidth='712px'
            >
              {description}
            </Text>
          </Flex>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

export function transformStaticSchoolHeroSectionProps({
  content,
  heroDescription,
}: {
  content: SchoolLpContent;
  /** Sanity school entry's `heroDescription`, editable without a deploy. */
  heroDescription: string;
}) {
  return {
    eyebrow: content.hero.eyebrow,
    title: content.hero.title,
    description: heroDescription,
    theme: {
      backgroundImage: content.theme.hero.backgroundImage,
      pennantImage: content.theme.hero.pennantImage,
      eyebrowColor: content.theme.colors.accentLight,
    },
  };
}

export type StaticSchoolHeroSectionProps = ReturnType<typeof transformStaticSchoolHeroSectionProps>;
